import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ImageViewer } from './PuzzleImageViewer';
import api from '../utils/api';
import { useToast } from './Toast';
import { useConfirm } from './Modal';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { formatBJ } from '../utils/datetime';
import { useBeijingToday } from '../utils/useBeijingToday';
import {
  Puzzle, Loader, ShieldAlert, Lock, LockOpen, X, Inbox, CheckCircle2, Ban, Pencil, RotateCcw, Search, Eye, Check, AlertTriangle,
} from 'lucide-react';

// 后台 · 证券识别记录：管理员看全量，代理商看名下。
//   待核实：用户识别失败后手填号码提交的记录 → 核实通过（分配序号、计入）/ 作废（号码回到未完成，不计入）
//   锁定：用户改的号码不在图片识别文字里 → 解锁后用户才能继续
//   已提交的记录：管理员/代理商可更正任何字段（系统记录修改人并重新合成最终图）——这是兜底：用户漏补、补错都在这里改。
//   用户自己只能在前台“图片管理”里补全识别失败留空的 微信号 / 昵称 / 操作微信号（补过的记录这里会显示“用户补全”）
//   错误上传：上传后被判为“重复提交 / 号码不在当前数据包内”的尝试。这两类不生成记录、不留图片，只留一行文字，保留 30 天
const FILTERS = [
  { key: 'pending', label: '待核实' },
  { key: 'all', label: '全部' },
  { key: 'counted', label: '已计入' },
  { key: 'locked', label: '已锁定' },
  { key: 'rejected', label: '已作废' },
  { key: 'errors', label: '错误上传' },
];
const ERR_REASON = {
  DUPLICATE:         { label: '重复提交', bg: 'rgba(255,149,0,0.14)', color: '#b26a00' },
  NOT_IN_MY_PACKAGE: { label: '不在包内', bg: 'rgba(255,59,48,0.10)', color: 'var(--c-danger)' },
};
const PAGE_SIZE = 20;
const FIELDS = [
  { key: 'phone', label: '电话' },
  { key: 'wechat_id', label: '微信号' },
  { key: 'nickname', label: '昵称' },
  { key: 'operator_wx', label: '操作微信号' },
];

function StatusBadge({ status }) {
  const ic = { display: 'inline-flex', alignItems: 'center', gap: 3 };
  if (status === 'confirmed') return <span className="badge badge-success" style={ic}><CheckCircle2 size={11} /> 已计入</span>;
  if (status === 'pending') return <span className="badge badge-warning" style={ic}><ShieldAlert size={11} /> 待核实</span>;
  if (status === 'rejected') return <span className="badge badge-danger" style={ic}><Ban size={11} /> 已作废</span>;
  if (status === 'locked') return <span className="badge badge-danger" style={ic}><Lock size={11} /> 已锁定</span>;
  return <span className="badge badge-info">草稿</span>;
}

export default function PuzzleRecordsAdmin({ userId }) {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { subscribe } = useSocket();
  const { user } = useAuth();
  const [filter, setFilter] = useState('pending');
  // 日期：默认 = 今天，过 0 点自动切到新的一天；交互和图片管理页同一套。
  // dateSel：null = 跟随今天；'' = 全部日期；'YYYY-MM-DD' = 手动选的那天
  const today = useBeijingToday();
  const [dateSel, setDateSel] = useState(null);
  const date = dateSel === null ? today : dateSel;
  const [keyword, setKeyword] = useState('');
  const [kwInput, setKwInput] = useState('');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [viewing, setViewing] = useState(null);   // 查看图片的记录
  const [editing, setEditing] = useState(null);   // 更正字段的记录
  const [offToday, setOffToday] = useState(0);    // “待核实/已锁定”里，不在当前日期、但仍未处理的条数（安全网提示）

  const load = useCallback(async (pg, append) => {
    setLoading(true);
    try {
      if (filter === 'errors') {
        // /puzzle/errors 的日期参数叫 date（不是 cdate），按上传当天筛
        const res = await api.get('/puzzle/errors', { params: { keyword, page: pg, pageSize: PAGE_SIZE, ...(date ? { date } : {}), ...(userId ? { userId } : {}) } });
        setList(prev => (append ? [...prev, ...res.data.list] : res.data.list));
        setTotal(res.data.total);
        setSummary(prev => ({ ...prev, errors: res.data.summary?.total || 0 }));
        setPage(pg);
        return;
      }
      // cdate：待核实/全部/已计入/已作废按“提交（确认）当天”筛，已锁定服务端会自动改按“创建当天”筛（它没有确认时间）
      const res = await api.get('/puzzle/records', { params: { status: filter, keyword, page: pg, pageSize: PAGE_SIZE, ...(date ? { cdate: date } : {}), ...(userId ? { userId } : {}) } });
      setList(prev => (append ? [...prev, ...res.data.list] : res.data.list));
      setTotal(res.data.total);
      setSummary(res.data.summary || {});
      setPage(pg);

      // 安全网：待核实/已锁定是要处理掉的队列，默认只看今天会把跨天没处理完的旧记录藏起来。
      // 停在“今天”（没手动选全部/某一天）时，在同一次加载里再查一次“不限日期”的条数（关键词、人员范围和上面保持一致），
      // 两个数一减就是不在今天、还没处理的条数。放在 load 里而不是单独的 effect，是为了两个数永远来自同一轮请求，
      // 不会拿着上一个筛选的旧 total 去减。翻页追加（append）不影响这个数，跳过。
      if (!append) {
        if (dateSel === null && (filter === 'pending' || filter === 'locked')) {
          try {
            const all = await api.get('/puzzle/records', { params: { status: filter, keyword, pageSize: 1, ...(userId ? { userId } : {}) } });
            setOffToday(Math.max(0, (all.data.total || 0) - (res.data.total || 0)));
          } catch { setOffToday(0); }
        } else {
          setOffToday(0);
        }
      }
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally { setLoading(false); }
  }, [filter, keyword, date, dateSel, userId, showToast]);

  useEffect(() => { load(1, false); }, [load]);
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => { if (event === 'puzzle_updated') load(1, false); });
  }, [subscribe, load]);

  const patchRecord = (rec) => setList(prev => prev.map(r => (r.id === rec.id ? { ...r, ...rec } : r)));

  const act = async (r, fn) => {
    setBusyId(r.id);
    try { await fn(); } catch (err) { showToast(err.response?.data?.error || '操作失败', 'error'); }
    finally { setBusyId(null); }
  };

  const review = (r, action) => act(r, async () => {
    if (action === 'reject') {
      const ok = await confirm(`确认作废 ${r.phone} 这条记录？`, { detail: '作废后不计入成功单数和结算，号码回到“未完成”，可再次被提交。', danger: true });
      if (!ok) return;
    }
    const res = await api.post('/puzzle/record/review', { recordId: r.id, action });
    if (res.data.status === 'OK') {
      showToast(action === 'approve' ? '已核实通过，计入成功单数' : '已作废', 'success');
      load(1, false);
    } else showToast('该记录已被处理', 'info');
  });

  const unlock = (r) => act(r, async () => {
    const res = await api.post('/puzzle/record/unlock', { recordId: r.id });
    if (res.data.record) { showToast('已解锁，用户可继续修改', 'success'); load(1, false); }
  });

  const recompose = (r) => act(r, async () => {
    const res = await api.post('/puzzle/record/recompose', { recordId: r.id }, { timeout: 60000 });
    if (res.data.record) { patchRecord(res.data.record); showToast('最终图已重新合成', 'success'); }
  });

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', gap: 0 }}>
          {[['待核实', summary.pending, 'var(--c-accent)'], ['已计入', summary.counted, 'var(--c-success)'], ['已锁定', summary.locked, 'var(--c-danger)'], ['已作废', summary.rejected, 'var(--c-text-secondary)'], ['错误上传', summary.errors, '#b26a00']].map(([label, v, color], i) => (
            <React.Fragment key={label}>
              {i > 0 && <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />}
              <div className="stat-card" style={{ flex: 1 }}>
                <div className="stat-value" style={{ color, fontSize: 22 }}>{v || 0}</div>
                <div className="stat-label">{label}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, flexShrink: 0, cursor: 'pointer',
            border: `1.5px solid ${filter === f.key ? 'var(--c-primary)' : 'var(--c-border)'}`,
            background: filter === f.key ? 'var(--c-primary)' : 'var(--c-surface)', color: filter === f.key ? '#fff' : 'var(--c-text-secondary)',
          }}>{f.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Search size={14} color="var(--c-text-tertiary)" style={{ position: 'absolute', left: 12, top: 14 }} />
          <input className="input" style={{ paddingLeft: 34, fontSize: 16 }} placeholder="搜索号码 / 昵称 / 微信号 / 包名，回车确认"
            value={kwInput} onChange={e => setKwInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setKeyword(kwInput.trim()); }}
            onBlur={() => setKeyword(kwInput.trim())} />
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <input className="input" type="date" value={date} max={today} onChange={e => setDateSel(e.target.value === today ? null : e.target.value)} style={{ fontSize: 16, width: 150 }} />
        </div>
        {dateSel === null
          ? <button className="btn btn-outline btn-sm" onClick={() => setDateSel('')} style={{ flexShrink: 0, height: 44, fontSize: 12 }}>全部日期</button>
          : <button className="btn btn-outline btn-sm" onClick={() => setDateSel(null)} style={{ flexShrink: 0, height: 44, fontSize: 12 }}>回到今天</button>}
      </div>

      {offToday > 0 && (filter === 'pending' || filter === 'locked') && (
        <button onClick={() => setDateSel('')} style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', textAlign: 'left', cursor: 'pointer',
          background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.3)', borderRadius: 10,
          padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#b26a00', fontWeight: 600,
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0 }} />
          另有 {offToday} 条{filter === 'pending' ? '待核实' : '已锁定'}记录不在今天，点这里查看全部
        </button>
      )}

      {list.length === 0 && !loading && (
        <div className="card empty-state" style={{ padding: '36px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><Inbox size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
          <p style={{ fontWeight: 600 }}>
            {filter === 'errors'
              ? (dateSel === '' ? '近 30 天没有错误上传' : dateSel === null ? '今天没有错误上传' : '这一天没有错误上传')
              : (dateSel === '' ? '暂无记录' : dateSel === null ? (filter === 'pending' ? '今天暂无待核实记录' : '今天暂无记录') : '这一天暂无记录')}
          </p>
        </div>
      )}

      {filter === 'errors' && list.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', lineHeight: 1.7, margin: '0 2px 8px' }}>
          这些上传被系统判为无效，没有生成记录、也没有保留图片，只记下号码和原因，保留 30 天。用户当时已在上传窗口看到提示。
        </div>
      )}
      {filter === 'errors' && list.map(r => {
        const rs = ERR_REASON[r.reason] || { label: r.reason, bg: 'var(--c-bg)', color: 'var(--c-text-secondary)' };
        return (
          <div key={r.id} className="card" style={{ padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700 }}>{r.phone || '（未识别到号码）'}</span>
              <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px', background: rs.bg, color: rs.color, flexShrink: 0 }}>{rs.label}</span>
            </div>
            <div style={{ fontSize: 13, marginTop: 4, fontWeight: 600 }}>
              {r.user_name}
              {r.submitted_by_name && <span style={{ fontWeight: 400, color: 'var(--c-text-secondary)', marginLeft: 6, fontSize: 12 }}>（由 {r.submitted_by_name} 代传）</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 3, lineHeight: 1.6 }}>
              {r.reason === 'DUPLICATE'
                ? `该号码已经提交过${r.first_user_name ? `（最早由 ${r.first_user_name} 提交）` : ''}${r.package_name ? ` · 数据包「${r.package_name}」` : ''}`
                : `号码不在他当前领取的数据包里${r.package_name ? `（当时按「${r.package_name}」核对）` : ''}`}
            </div>
            <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 3 }}>{formatBJ(r.created_at)}</div>
          </div>
        );
      })}

      {filter !== 'errors' && list.map(r => {
        const busy = busyId === r.id;
        const thumb = r.images?.combined || r.images?.chat;
        return (
          <div key={r.id} className="card" style={{ padding: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div onClick={() => setViewing(r)} style={{
                width: 56, height: 56, borderRadius: 10, overflow: 'hidden', flexShrink: 0, background: 'var(--c-bg)', border: '1px solid var(--c-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-in',
              }}>
                {thumb ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }} /> : <Puzzle size={20} color="var(--c-text-tertiary)" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>{r.phone || '—'}</span>
                  <StatusBadge status={r.status} />
                  {r.manual_filled && <span className="badge badge-purple">手填</span>}
                  {/* 用户提交后自己补全过识别失败的字段：管理员复核时留意，补错了直接“更正字段” */}
                  {r.user_fixed && <span className="badge badge-info">用户补全</span>}
                  {/* 已提交但最终图缺失（合成失败）：点“重新合成”；导出会自动跳过这类记录 */}
                  {(r.status === 'confirmed' || r.status === 'pending') && !r.images?.combined && !r.files_cleaned && <span className="badge badge-danger">无最终图</span>}
                  {r.is_settled && <span className="badge badge-info">已结算</span>}
                  {r.files_cleaned ? <span className="badge" style={{ background: 'var(--c-bg)', color: 'var(--c-text-tertiary)' }}>已清理</span>
                    : r.exported && <span className="badge badge-info">已导出</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.user_name} · {r.package_name}{r.seq_no != null ? ` · 第 ${r.seq_no} 单` : ''}
                </div>
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 1 }}>
                  {r.nickname || '无昵称'} · {r.wechat_id || '无微信号'} · {formatBJ(r.confirmed_at || r.created_at)}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {r.status === 'pending' && (
                <>
                  <button className="btn btn-success btn-sm" disabled={busy} onClick={() => review(r, 'approve')}><Check size={14} /> 核实通过</button>
                  <button className="btn btn-sm" disabled={busy} onClick={() => review(r, 'reject')}
                    style={{ background: 'rgba(255,59,48,0.1)', color: 'var(--c-danger)' }}><Ban size={14} /> 作废</button>
                </>
              )}
              {r.status === 'locked' && (
                <button className="btn btn-accent btn-sm" disabled={busy || (r.user_id === user?.id && user?.role !== 'admin')} onClick={() => unlock(r)}><LockOpen size={14} /> 解锁</button>
              )}
              {/* 已导出并清理过的记录：原始截图没了，不能再更正字段、重新合成 */}
              {!r.files_cleaned && (r.status === 'confirmed' || r.status === 'pending' || r.status === 'locked' || r.status === 'draft') && (
                <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => setEditing(r)}><Pencil size={14} /> {r.status === 'confirmed' || r.status === 'pending' ? '更正字段' : '代改字段'}</button>
              )}
              {!r.files_cleaned && (r.status === 'confirmed' || r.status === 'pending') && (
                <button className="btn btn-outline btn-sm" disabled={busy} onClick={() => recompose(r)}>
                  {busy ? <Loader size={14} className="spin" /> : <RotateCcw size={14} />} 重新合成
                </button>
              )}
              <button className="btn btn-outline btn-sm" onClick={() => setViewing(r)}><Eye size={14} /> 查看图片</button>
            </div>
          </div>
        );
      })}

      {loading && <p style={{ textAlign: 'center', padding: 16, color: 'var(--c-text-secondary)' }}>加载中...</p>}
      {!loading && list.length < total && (
        <button className="btn btn-outline" onClick={() => load(page + 1, true)} style={{ height: 42 }}>加载更多</button>
      )}

      {viewing && <ImageViewer rec={viewing} onClose={() => setViewing(null)} />}
      {editing && (
        <EditModal rec={editing} onClose={() => setEditing(null)}
          onSaved={(rec) => { patchRecord(rec); setEditing(null); showToast('已保存', 'success'); }} />
      )}
    </>
  );
}

export { ImageViewer };

function EditModal({ rec, onClose, onSaved }) {
  const { showToast } = useToast();
  const [vals, setVals] = useState({ phone: rec.phone || '', wechat_id: rec.wechat_id || '', nickname: rec.nickname || '', operator_wx: rec.operator_wx || '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setSaving(true); setErr('');
    let cur = rec;
    try {
      for (const f of FIELDS) {
        const v = String(vals[f.key] ?? '').trim();
        if (v === String(cur[f.key] ?? '')) continue;
        if (f.key === 'phone' && !v) { setErr('电话不能为空'); return; }
        const res = await api.post('/puzzle/ocr-edit', { recordId: rec.id, field: f.key, value: v }, { timeout: 60000 });
        if (res.data.status !== 'OK') { setErr(res.data.message || `${f.label}保存失败`); return; }
        cur = res.data.record;
      }
      onSaved(cur);
    } catch (e) {
      showToast(e.response?.data?.error || '保存失败', 'error');
    } finally { setSaving(false); }
  };

  return createPortal(
    <div onClick={() => !saving && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--c-surface)', borderRadius: '22px 22px 0 0', width: '100%', maxWidth: 480, padding: '18px 18px 24px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 17, fontWeight: 700, flex: 1 }}>{rec.status === 'confirmed' || rec.status === 'pending' ? '更正字段' : '代改字段'}</span>
          <button onClick={onClose} disabled={saving} style={{ background: 'var(--c-bg)', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} color="var(--c-text-secondary)" />
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>
          管理员/代理商更正不校验图片文字（电话仍须在该数据包内）；已提交的记录会留下修改人，并自动重新合成最终图。
          {rec.user_fixed && <><br />这条记录用户自己补全过识别失败的字段，请对照原图确认没有补错。</>}
        </div>
        {rec.exported && !rec.files_cleaned && (
          <div style={{ fontSize: 12, color: '#9a6400', background: 'rgba(255,149,0,0.08)', border: '1px solid rgba(255,149,0,0.25)', borderRadius: 10, padding: '8px 10px', lineHeight: 1.6, marginBottom: 12 }}>
            这条记录已经导出过：改完最终图会更新，但之前导出的压缩包里还是旧图，需要用新图请重新导出（勾选“包含已导出过的”）。
          </div>
        )}
        {FIELDS.map(f => (
          <div key={f.key} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 4 }}>{f.label}</div>
            <input className="input" style={{ fontSize: 16, padding: '10px 12px' }} value={vals[f.key]} onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))} />
          </div>
        ))}
        {err && <div style={{ fontSize: 13, color: 'var(--c-danger)', marginBottom: 8 }}>{err}</div>}
        <button className="btn btn-primary" onClick={save} disabled={saving} style={{ height: 46, marginTop: 4 }}>
          {saving ? <><Loader size={16} className="spin" /> 保存中…</> : '保存'}
        </button>
      </div>
    </div>,
    document.body
  );
}
